import { type CartesiaLanguage } from "./lang";
import type { Cartesia } from "@cartesia/cartesia-js";

type CartesiaVoiceBase = {
  name?: string;
  gender?: "masculine" | "feminine";
  description?: string;
  language: CartesiaLanguage;
} & Cartesia.TtsRequestIdSpecifier;

export type CartesiaVoice = CartesiaVoiceBase & {
  provider: "cartesia";
  modelId: "sonic-3";
};

const _CARTESIA_SONIC_3_VOICES: CartesiaVoiceBase[] = [
  {
    id: "729651dc-c6c3-4ee5-97fa-350da1f88600",
    name: "Jake",
    mode: "id",
    gender: "masculine",
    language: "en",
  },
  {
    id: "4df027cb-2920-4a1f-8c34-f21529d5c3fe",
    name: "Carson",
    mode: "id",
    gender: "masculine",
    language: "en",
  },
  {
    id: "1242fb95-7ddd-44ac-8a05-9e8a22a6137d",
    name: "Cindy",
    mode: "id",
    gender: "feminine",
    language: "en",
  },
  {
    id: "f786b574-daa5-4673-aa0c-cbe3e8534c02",
    name: "Katie",
    mode: "id",
    gender: "feminine",
    language: "en",
  },
  {
    id: "5c3c89e5-535f-43ef-b14d-f8ffe148c1f0",
    name: "French Narrator Man",
    mode: "id",
    gender: "masculine",
    description:
      "This voice is even and rich, perfect for narrating content in French",
    language: "fr",
  },
  {
    id: "8832a0b5-47b2-4751-bb22-6a8e2149303d",
    name: "French Narrator Lady",
    mode: "id",
    gender: "feminine",
    description:
      "This voice is velvety and neutral, suited for narrating content in French",
    language: "fr",
  },
  {
    id: "e00dd3df-19e7-4cd4-827a-7ff6687b6954",
    name: "Lukas",
    mode: "id",
    gender: "masculine",
    description:
      "Lukas is a confident and approachable German voice, perfect for phone systems and conversational AI. His tone is warm yet professional, ensuring smooth and engaging interactions.",
    language: "de",
  },
  {
    id: "38aabb6a-f52b-4fb0-a3d1-988518f4dc06",
    name: "Alina",
    mode: "id",
    gender: "feminine",
    description:
      "Alina is a warm, engaging German voice designed for seamless and natural conversations. With a smooth, friendly tone and clear articulation, she is perfect for phone systems, virtual assistants, and customer service interactions.",
    language: "de",
  },
  {
    id: "79743797-2087-422f-8dc7-86f9efca85f1",
    name: "Mateo",
    mode: "id",
    gender: "masculine",
    description:
      "Mateo is a confident and engaging Spanish voice, perfect for conversational AI and phone interactions. His tone is warm, clear, and naturally expressive, ensuring smooth and natural conversations.",
    language: "es",
  },
  {
    id: "cefcb124-080b-4655-b31f-932f3ee743de",
    name: "Elena",
    mode: "id",
    gender: "feminine",
    description:
      "Elena is a warm and inviting Spanish voice, designed for seamless and natural conversations. Her speech is clear, articulate, and expressive, making her ideal for phone systems, virtual assistants, and customer support.",
    language: "es",
  },
  {
    id: "6a360542-a117-4ed5-9e09-e8bf9b05eabb",
    name: "Tiago",
    mode: "id",
    gender: "masculine",
    description:
      "A calm and clear Portuguese male voice, perfect for narration and engaging conversations.",
    language: "pt",
  },
  {
    id: "d4b44b9a-82bc-4b65-b456-763fce4c52f9",
    name: "Clara",
    mode: "id",
    gender: "feminine",
    description:
      "A friendly and natural Portuguese female voice that makes every conversation feel engaging and effortless.",
    language: "pt",
  },
  {
    id: "653b9445-ae0c-4312-a3ce-375504cff31e",
    name: "Liu",
    mode: "id",
    gender: "masculine",
    description:
      "A conversational Chinese man, great for phone calls and voice messages.",
    language: "zh",
  },
  {
    id: "e90c6678-f0d3-4767-9883-5d0ecf5894a8",
    name: "Yue",
    mode: "id",
    gender: "feminine",
    description:
      "This voice is friendly and inviting, perfect for casual conversations in Chinese",
    language: "zh",
  },
  {
    id: "6b92f628-be90-497c-8f4c-3b035002df71",
    name: "Kenji",
    mode: "id",
    gender: "masculine",
    description:
      "A calm and clear Japanese male voice, perfect for narration and engaging conversations.",
    language: "ja",
  },
  {
    id: "59d4fd2f-f5eb-4410-8105-58db7661144f",
    name: "Yuki",
    mode: "id",
    gender: "feminine",
    description:
      "A calm and clear Japanese female voice, perfect for narration and engaging conversations.",
    language: "ja",
  },
  {
    id: "791d5162-d5eb-40f0-8189-f19db44611d8",
    name: "Ayush",
    mode: "id",
    gender: "masculine",
    description:
      "A confident, young Indian male voice, ideal for delivering demos, instructions, and customer support",
    language: "hi",
  },
  {
    id: "9cebb910-d4b7-4a4a-85a4-12c79137724c",
    name: "Aarti",
    mode: "id",
    gender: "feminine",
    description:
      "A warm and friendly feminine voice with a smooth Indian accent, perfect for engaging storytelling, relatable dialogue, and effortless, everyday conversations that feel authentic and inviting.",
    language: "hi",
  },
  {
    id: "e5923af7-a329-4e9b-b95a-5ace4a083535",
    name: "Lucio",
    mode: "id",
    gender: "masculine",
    description:
      "Luca is a charismatic and engaging Italian voice, perfect for conversational AI and phone interactions. His tone is warm, smooth, and naturally expressive, making every dialogue feel authentic and personal.",
    language: "it",
  },
  {
    id: "d609f27f-f1a4-410f-85bb-10037b4fba99",
    name: "Francesca",
    mode: "id",
    gender: "feminine",
    description:
      "A smooth and articulate Italian female voice that enhances storytelling and natural dialogue.",
    language: "it",
  },
  {
    id: "af6beeea-d732-40b6-8292-73af0035b740",
    name: "Byungtae",
    mode: "id",
    gender: "masculine",
    description:
      "A warm, resonant Korean male narrator voice with clear enunciation and a balanced tone, ideal for storytelling, audiobooks, or instructional content.",
    language: "ko",
  },
  {
    id: "304fdbd8-65e6-40d6-ab78-f9d18b9efdf9",
    name: "Jihyun",
    mode: "id",
    gender: "feminine",
    description:
      "A smooth Korean female voice with a gentle, expressive tone, perfect for conversations.",
    language: "ko",
  },
  {
    id: "af482421-80f4-4379-b00c-a118def29cde",
    name: "Lucas",
    mode: "id",
    gender: "masculine",
    description:
      "A smooth and articulate Dutch male voice that enhances storytelling and natural dialogue.",
    language: "nl",
  },
  {
    id: "0eb213fe-4658-45bc-9442-33a48b24b133",
    name: "Sanne",
    mode: "id",
    gender: "feminine",
    description:
      "A calm and clear Dutch female voice, perfect for narration and engaging conversations.",
    language: "nl",
  },
  {
    id: "2a3503b2-b6b6-4534-a224-e8c0679cec4a",
    name: "Jakub",
    mode: "id",
    gender: "masculine",
    description:
      "A calm and clear Polish male voice, perfect for narration and engaging conversations.",
    language: "pl",
  },
  {
    id: "dcf62f33-7cff-4f20-85b2-2efaa68cbc32",
    name: "Zofia",
    mode: "id",
    gender: "feminine",
    description:
      "A warm and expressive Polish female voice, great for audiobooks and clear communication.",
    language: "pl",
  },
  {
    id: "888b7df4-e165-4852-bfec-0ab2b96aaa46",
    name: "Dmitri",
    mode: "id",
    gender: "masculine",
    description:
      "Approachable adult male voice with a relaxed tone for casual chats and everyday conversations.",
    language: "ru",
  },
  {
    id: "064b17af-d36b-4bfb-b003-be07dba1b649",
    name: "Tatiana",
    mode: "id",
    gender: "feminine",
    description:
      "A warm and expressive Russian female voice, great for audiobooks and clear communication.",
    language: "ru",
  },
  {
    id: "0caedb75-417f-4e36-9b64-c21354cb94c8",
    name: "Lars",
    mode: "id",
    gender: "masculine",
    description:
      "A calm and clear Swedish male voice, perfect for narration and smooth conversations.",
    language: "sv",
  },
  {
    id: "6c6b05bf-ae5f-4013-82ab-7348e99ffdb2",
    name: "Freja",
    mode: "id",
    gender: "feminine",
    description:
      "A warm and expressive Swedish female voice, great for audiobooks and clear communication.",
    language: "sv",
  },
  {
    id: "c1cfee3d-532d-47f8-8dd2-8e5b2b66bf1d",
    name: "Taylan",
    mode: "id",
    gender: "masculine",
    description:
      "A smooth and articulate Turkish male voice, ideal for storytelling and dynamic speech applications.",
    language: "tr",
  },
  {
    id: "fa7bfcdc-603c-4bf1-a600-a371400d2f8c",
    name: "Leyla",
    mode: "id",
    gender: "feminine",
    description:
      "A smooth and expressive Turkish female voice, great for storytelling and conversational speech.",
    language: "tr",
  },
  {
    id: "002622d8-19d0-4567-a16a-f99c7397c062",
    name: "Huda",
    mode: "id",
    gender: "feminine",
    description: "Natural voice for clear, engaging conversations",
    language: "ar",
  },
  {
    id: "f1cdfb4a-bf7d-4e83-916e-8f0802278315",
    name: "Walid",
    mode: "id",
    gender: "masculine",
    description:
      "Warm, confident voice for clear, customer-facing conversations",
    language: "ar",
  },
  {
    id: "59ba7dee-8f9a-432f-a6c0-ffb33666b654",
    name: "Pooja",
    mode: "id",
    gender: "feminine",
    description:
      "Soft-spoken adult female voice for casual conversations and natural dialogue",
    language: "bn",
  },
  {
    id: "2ba861ea-7cdc-43d1-8608-4045b5a41de5",
    name: "Rubel",
    mode: "id",
    gender: "masculine",
    description:
      "Casual adult male voice for everyday conversations and relatable dialogue",
    language: "bn",
  },
  {
    id: "fcbecbcc-0cef-4615-8b5a-712fe1b39dd0",
    name: "Ivana",
    mode: "id",
    gender: "feminine",
    description:
      "Clear and authoritative adult female voice for giving instructions and professional communication",
    language: "bg",
  },
  {
    id: "d132064c-b931-4a80-bf0d-02a331ec4572",
    name: "Georgi",
    mode: "id",
    gender: "masculine",
    description:
      "Friendly adult male with a casual tone for everyday conversations and relatable dialogue",
    language: "bg",
  },
  {
    id: "2a2624ad-bd06-4563-81fd-0519742e25d2",
    name: "Petra",
    mode: "id",
    gender: "feminine",
    description:
      "Firm and strict adult female voice for storytelling, guidance, and instructional use cases",
    language: "hr",
  },
  {
    id: "a1a16724-b1f3-4b27-9e47-8a175115e93c",
    name: "Ivan",
    mode: "id",
    gender: "masculine",
    description: "Relaxed adult male voice for easygoing conversations",
    language: "hr",
  },
  {
    id: "bdc4a3ce-2e22-4398-8cd6-76b7160d2298",
    name: "Jana",
    mode: "id",
    gender: "feminine",
    description:
      "Clear and crisp female voice with a casual yet commanding tone for customer support and guidance",
    language: "cs",
  },
  {
    id: "89266bab-6e15-455d-8654-e18c440b0656",
    name: "Petr",
    mode: "id",
    gender: "masculine",
    description:
      "Resonant adult male voice, reminiscent of a priest delivering a sermon for narrations and formal reading",
    language: "cs",
  },
  {
    id: "c323c793-41f9-47b8-99dc-9b44b0440b84",
    name: "Katrine",
    mode: "id",
    gender: "feminine",
    description:
      "Soft and calm adult female voice for meditation, relaxation, and gentle conversations",
    language: "da",
  },
  {
    id: "926e0766-f380-4d77-aeb0-9aa4ebb16b38",
    name: "Soren",
    mode: "id",
    gender: "masculine",
    description:
      "Voice with confident, businesslike tone and clear precision, perfect for professional dialogue",
    language: "da",
  },
  {
    id: "65c34eec-42c9-4a75-a8bd-b676fb847b72",
    name: "Helmi",
    mode: "id",
    gender: "feminine",
    description:
      "Friendly adult female voice for casual conversations and everyday chat",
    language: "fi",
  },
  {
    id: "ae1a833b-0d95-4b7f-8d05-d6418c6f8049",
    name: "Mikko",
    mode: "id",
    gender: "masculine",
    description:
      "Firm and strong adult male voice for narration, audiobooks, and authoritative communication",
    language: "fi",
  },
  {
    id: "0bfbea6c-2f8f-4f86-b411-aa2316561e36",
    name: "Tamara",
    mode: "id",
    gender: "feminine",
    description:
      "Professional and clear adult female voice for customer service, guidance, and supportive interactions",
    language: "ka",
  },
  {
    id: "dbebd077-80cb-4bcf-b43b-4552f96341bb",
    name: "Levan",
    mode: "id",
    gender: "masculine",
    description:
      "Casual and approachable adult male voice for customer support and everyday conversations",
    language: "ka",
  },
  {
    id: "50849023-76e9-46c7-af52-9ec39888a165",
    name: "Despina",
    mode: "id",
    gender: "feminine",
    description:
      "Warm yet authoritative middle-aged female voice, with a motherly tone for guidance, reassurance, and clear instruction",
    language: "el",
  },
  {
    id: "b45eba5b-2215-4da7-9c7c-121c95ed7b81",
    name: "Nikos",
    mode: "id",
    gender: "masculine",
    description:
      "Nostalgic, middle-aged male voice with an old-radio tone for historical reenactments, vintage ads, or period storytelling",
    language: "el",
  },
  {
    id: "4590a461-bc68-4a50-8d14-ac04f5923d22",
    name: "Isha",
    mode: "id",
    gender: "feminine",
    description:
      "Youthful male voice with a clear and approachable tone for narration, educational content, and voiceover",
    language: "gu",
  },
  {
    id: "91925fe5-42ee-4ebe-96c1-c84b12a85a32",
    name: "Amit",
    mode: "id",
    gender: "masculine",
    description:
      "Friendly young adult male voice for casual conversations and natural everyday dialogue",
    language: "gu",
  },
  {
    id: "1daba551-67af-465e-a189-f91495aa2347",
    name: "Yael",
    mode: "id",
    gender: "feminine",
    description:
      "Voice with relaxed tone and friendly warmth, ideal for conversational dialogue and everyday narration",
    language: "he",
  },
  {
    id: "3e32f3c5-9ac0-4192-9994-87fdb277120f",
    name: "Noam",
    mode: "id",
    gender: "masculine",
    description:
      "Clear and authoritative adult male voice for announcements, broadcasts, and formal presentations",
    language: "he",
  },
  {
    id: "e97c3b37-1aa5-46af-afb7-9545086aaa92",
    name: "Eszter",
    mode: "id",
    gender: "feminine",
    description:
      "Clear and cheerful adult female voice for customer support and friendly interactions",
    language: "hu",
  },
  {
    id: "36e0c00b-1bfd-4ad7-a0e8-928d4cadca00",
    name: "Gabor",
    mode: "id",
    gender: "masculine",
    description:
      "Firm and well-paced adult male voice for customer support, guidance, and professional communication",
    language: "hu",
  },
  {
    id: "b441c4fd-4910-4c55-ae56-f0291057e2cc",
    name: "Siti",
    mode: "id",
    gender: "feminine",
    description:
      "Cheerful and optimistic adult female voice for brand placements, advertisements, and engaging storytelling",
    language: "id",
  },
  {
    id: "a053f6bc-7df4-40de-96d4-de026bc47ce8",
    name: "Andi",
    mode: "id",
    gender: "masculine",
    description:
      "Expressive adult male voice with an upbeat tone for advertisements, promotions, and lively announcements",
    language: "id",
  },
  {
    id: "83604597-55fa-4ccc-8357-730b313f353f",
    name: "Aisyah",
    mode: "id",
    gender: "feminine",
    description:
      "Friendly and upbeat adult female voice for casual conversations and engaging everyday dialogue",
    language: "ms",
  },
  {
    id: "8281db18-6ac5-47bb-91a8-ce23a1f1d951",
    name: "Faiz",
    mode: "id",
    gender: "masculine",
    description:
      "Warm and fatherly adult male voice for casual conversations and comforting dialogue",
    language: "ms",
  },
  {
    id: "5c32dce6-936a-4892-b131-bafe474afe5f",
    name: "Anika",
    mode: "id",
    gender: "feminine",
    description:
      "Energetic and approachable adult female voice for sales conversations, casual chat, and customer support",
    language: "mr",
  },
  {
    id: "f227bc18-3704-47fe-b759-8c78a450fdfa",
    name: "Suresh",
    mode: "id",
    gender: "masculine",
    description:
      "Clear and well-enunciated adult male voice for instructions, narrations, and professional communication",
    language: "mr",
  },
  {
    id: "d6dca1b6-cdd8-4e9c-823c-e03979261740",
    name: "Lars",
    mode: "id",
    gender: "masculine",
    description:
      "Approachable adult male voice with a relaxed tone for casual conversations and everyday interactions",
    language: "no",
  },
  {
    id: "34acfaee-c556-41ee-a5f6-c687fb20357c",
    name: "Andrada",
    mode: "id",
    gender: "feminine",
    description:
      "Clear and monotone adult female voice for instructions formal announcements, and straightforward communication",
    language: "ro",
  },
  {
    id: "3f64ef99-d87b-4b51-b217-df7351f7886a",
    name: "Andrei",
    mode: "id",
    gender: "masculine",
    description:
      "Casual yet firm middle-aged male voice for everyday conversations, guidance, and professional dialogue",
    language: "ro",
  },
  {
    id: "abf68668-6549-462c-8426-1fa7b466b91d",
    name: "Katarina",
    mode: "id",
    gender: "feminine",
    description:
      "Warm and approachable adult female voice for customer service, support, and friendly communication",
    language: "sk",
  },
  {
    id: "ca590fdc-df56-4d2e-94a4-ef5b423c7ddf",
    name: "Peter",
    mode: "id",
    gender: "masculine",
    description:
      "Steady and articulate adult male voice for narrations, audiobooks, and professional storytelling",
    language: "sk",
  },
  {
    id: "9261664a-c3d0-4200-9038-5466bcf3a09c",
    name: "Luz",
    mode: "id",
    gender: "feminine",
    description:
      "Natural and conversational adult female voice for everyday dialogue, friendly interactions, and support",
    language: "tl",
  },
  {
    id: "c4cbcb7d-d9fa-4eac-b547-46831718ef58",
    name: "Angelo",
    mode: "id",
    gender: "masculine",
    description:
      "Gentle young adult male voice with a calm and approachable tone for casual conversations, support, and narrations",
    language: "tl",
  },
  {
    id: "25d2c432-139c-4035-bfd6-9baaabcdd006",
    name: "Kavya",
    mode: "id",
    gender: "feminine",
    description:
      "Friendly voice with natural tone and smooth flow, ideal for everyday conversations",
    language: "ta",
  },
  {
    id: "d2870b91-1b4c-47ab-81a8-3718d8e9c222",
    name: "Arun",
    mode: "id",
    gender: "masculine",
    description:
      "Expressive adult voice with a lively tone for storytelling, narrations, and engaging cultural content",
    language: "ta",
  },
  {
    id: "07bc462a-c644-49f1-baf7-82d5599131be",
    name: "Sindhu",
    mode: "id",
    gender: "feminine",
    description:
      "Clear and natural adult female voice for casual conversations and everyday interactions",
    language: "te",
  },
  {
    id: "ebecd063-10f4-422e-a8ff-556ce5c4d4e4",
    name: "Pavan",
    mode: "id",
    gender: "masculine",
    description:
      "Voice with energetic clarity and upbeat tone, ideal for customer support and engaging communication",
    language: "te",
  },
  {
    id: "ccc7bb22-dcd0-42e4-822e-0731b950972f",
    name: "Suda",
    mode: "id",
    gender: "feminine",
    description:
      "Expressive adult female voice with lively pronunciation for storytelling, advertisements, and engaging dialogue",
    language: "th",
  },
  {
    id: "5de076e9-7b28-4442-b279-e7d80d573505",
    name: "Somchai",
    mode: "id",
    gender: "masculine",
    description:
      "Upbeat and confident adult male voice for commercials, brand promotions, and lively presentations",
    language: "th",
  },
  {
    id: "05ffab9c-d380-4909-8375-cd12f59238c3",
    name: "Oleh",
    mode: "id",
    gender: "masculine",
    description:
      "Approachable adult male voice with a professional tone for casual conversations and everyday interactions",
    language: "uk",
  },
  {
    id: "935a9060-373c-49e4-b078-f4ea6326987a",
    name: "Linh",
    mode: "id",
    gender: "feminine",
    description:
      "Voice with gentle tone and natural warmth, perfect for natural conversations and friendly dialogue",
    language: "vi",
  },
  {
    id: "0e58d60a-2f1a-4252-81bd-3db6af45fb41",
    name: "Minh",
    mode: "id",
    gender: "masculine",
    description:
      "Easygoing adult male voice with a friendly tone for light conversation, casual dialogue, and everyday chat",
    language: "vi",
  },
  {
    id: "b426013c-002b-4e89-8874-8cd20b68373a",
    name: "Latha",
    mode: "id",
    gender: "feminine",
    description:
      "Bright and clear adult female voice for customer support, greetings, and welcoming guests",
    language: "ml",
  },
  {
    id: "374b80da-e622-4dfc-90f6-1eeb13d331c9",
    name: "Vijay",
    mode: "id",
    gender: "masculine",
    description:
      "Friendly and easygoing male voice for casual conversations and everyday support",
    language: "ml",
  },
  {
    id: "7c6219d2-e8d2-462c-89d8-7ecba7c75d65",
    name: "Divya",
    mode: "id",
    gender: "feminine",
    description:
      "Lively and cheerful adult female voice for product advertisements, upbeat promotions, and happy storytelling",
    language: "kn",
  },
  {
    id: "6baae46d-1226-45b5-a976-c7f9b797aae2",
    name: "Prakash",
    mode: "id",
    gender: "masculine",
    description:
      "Firm and articulate middle-aged male voice for lectures, presentations, and instructional content",
    language: "kn",
  },
  {
    id: "991c62ce-631f-48b0-8060-2a0ebecbd15b",
    name: "Jaspreet",
    mode: "id",
    gender: "feminine",
    description:
      "Expressive adult female voice with an engaging tone for commercials, promotions, and lively announcements",
    language: "pa",
  },
  {
    id: "8bacd442-a107-4ec1-b6f1-2fcb3f6f4d56",
    name: "Gurpreet",
    mode: "id",
    gender: "masculine",
    description:
      "Soft and caring adult male voice for empathetic conversations, support, and reassuring dialogue",
    language: "pa",
  },
];

const CARTESIA_SONIC_3_VOICES = _CARTESIA_SONIC_3_VOICES.map((voice) => ({
  ...voice,
  provider: "cartesia" as const,
  modelId: "sonic-3" as const,
}));

export const CARTESIA_LANGUAGE_TO_VOICES = CARTESIA_SONIC_3_VOICES.reduce(
  (acc, voice) => {
    acc[voice.language] = [...(acc[voice.language] || []), voice];
    return acc;
  },
  {} as Record<CartesiaLanguage, CartesiaVoice[]>
);

export const VOICE_ID_TO_CARTESIA_VOICE = CARTESIA_SONIC_3_VOICES.reduce(
  (acc, voice) => {
    acc[voice.id] = voice;
    return acc;
  },
  {} as Record<string, CartesiaVoice>
);
